/**
 * Main Chatbot Widget Component
 * Handles the entire chat interface and conversation flow
 */
import React, { useState, useEffect, useRef } from 'react';
import ChatWindow from './components/ChatWindow';
import ChatButton from './components/ChatButton';
import { translations } from './translations';
import { ChatFlow } from './services/ChatFlow';
import './styles.css';

const ICON_POSITION_KEY = 'afgcoin_chatbot_icon_position';
const SESSION_TOKEN_KEY = 'afgcoin_chatbot_session';
const ICON_SIZE = 56;
const WINDOW_WIDTH = 380;
const WINDOW_HEIGHT = 600;
/** Movement (px) above which we treat the gesture as a drag; below = tap (open chat). Reduces multiple-tap on mobile. */
const DRAG_THRESHOLD_PX = 10;

function loadSavedIconPosition() {
  try {
    const saved = localStorage.getItem(ICON_POSITION_KEY);
    if (saved) {
      const { x, y } = JSON.parse(saved);
      if (typeof x === 'number' && typeof y === 'number') return { x, y };
    }
  } catch (_) {}
  return null;
}

function clampIconPosition(x, y, isOpen) {
  const w = isOpen ? WINDOW_WIDTH : ICON_SIZE;
  const h = isOpen ? WINDOW_HEIGHT : ICON_SIZE;
  const maxX = typeof window !== 'undefined' ? Math.max(0, window.innerWidth - w) : 0;
  const maxY = typeof window !== 'undefined' ? Math.max(0, window.innerHeight - h) : 0;
  return {
    x: Math.max(0, Math.min(x, maxX)),
    y: Math.max(0, Math.min(y, maxY))
  };
}

const ChatbotWidget = ({ apiUrl, position = 'bottom-right', onCloseComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [iconPosition, setIconPosition] = useState(loadSavedIconPosition);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [sessionToken, setSessionToken] = useState(null);
  const [chatState, setChatState] = useState('identification');
  const [messages, setMessages] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false); // Prevent multiple simultaneous sends
  const [identificationError, setIdentificationError] = useState(null); // In-widget error instead of native alert
  const [isTyping, setIsTyping] = useState(false); // Typing indicator for AI/agent
  const messagesLoadedRef = useRef(false); // Track if initial history has been loaded
  const typingStartTimeRef = useRef(null); // Track when typing started for minimum duration
  const chatFlow = useRef(new ChatFlow(apiUrl));
  const widgetRef = useRef(null);
  const humanPollingIntervalRef = useRef(null);
  const dragStartRef = useRef(null);
  const didDragRef = useRef(false);
  const lastSavedPositionRef = useRef(iconPosition);

  /**
   * Load initial message history from backend (called once on session resume/init)
   * This loads the full conversation history initially.
   * After initial load, messages are appended, not replaced.
   */
  const loadInitialHistory = async (token) => {
    if (messagesLoadedRef.current) {
      // History already loaded, don't reload
      return;
    }
    
    try {
      const history = await chatFlow.current.getChatHistory(token);
      if (history && history.messages) {
        console.log(`Loading initial history: ${history.messages.length} messages`);
        setMessages(history.messages);
        messagesLoadedRef.current = true;
      } else {
        console.log('No messages in initial history');
        setMessages([]);
        messagesLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to load initial history from backend:', error);
      // Don't clear messages on error - keep existing state
    }
  };

  /**
   * Append a new message to the message list
   * CRITICAL: This appends, never replaces existing messages
   */
  const appendMessage = (newMessage) => {
    setMessages(prevMessages => {
      // Check if message already exists (prevent duplicates)
      const exists = prevMessages.some(msg => msg.id === newMessage.id);
      if (exists) {
        console.log(`Message ${newMessage.id} already exists, skipping append`);
        return prevMessages;
      }
      
      // Append new message to existing messages
      console.log(`Appending message: id=${newMessage.id}, role=${newMessage.role}`);
      return [...prevMessages, newMessage];
    });
  };

  /**
   * Merge backend messages with existing messages (append-only, no replacement)
   * CRITICAL: This merges messages from backend without replacing existing ones.
   * Only adds messages that don't already exist in the current state.
   * Preserves all existing messages and their order.
   * Uses string IDs for comparison so backend (number) and frontend (string temp ids) match correctly.
   */
  const mergeMessages = (backendMessages) => {
    if (!backendMessages || !Array.isArray(backendMessages)) {
      return;
    }

    setMessages(prevMessages => {
      const existingIds = new Set(prevMessages.map(msg => String(msg.id)));
      const newMessages = backendMessages.filter(msg => !existingIds.has(String(msg.id)));
      if (newMessages.length === 0) return prevMessages;

      const allMessages = [...prevMessages, ...newMessages];
      allMessages.sort((a, b) => {
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        return timeA - timeB;
      });
      return allMessages;
    });
  };

  // On mount: restore session_token from localStorage so the same session persists across refresh/reopen.
  // Do NOT remove the token here; only clear when conversation is finished or user resets.
  useEffect(() => {
    const saved = localStorage.getItem(SESSION_TOKEN_KEY);
    if (saved && typeof saved === 'string' && saved.trim()) {
      setSessionToken(saved.trim());
      setChatState('human_waiting'); // Show chat and allow polling; first poll can correct state
      messagesLoadedRef.current = false;
    } else {
      setSessionToken(null);
      setChatState('identification');
      messagesLoadedRef.current = false;
    }
    setMessages([]);
    setUser(null);
    setCurrentLanguage('en');
    setIsOpen(false);
  }, []);

  // When widget opens: do NOT clear session or localStorage. Keep same session so admin and widget stay in sync.
  useEffect(() => {
    if (isOpen) {
      setIdentificationError(null);
    }
  }, [isOpen]);

  // Clear human-support polling (stops when widget closes or user leaves human support)
  const clearHumanPolling = () => {
    if (humanPollingIntervalRef.current) {
      clearInterval(humanPollingIntervalRef.current);
      humanPollingIntervalRef.current = null;
    }
  };

  // Start polling for admin replies when in human_waiting. Calls GET /api/chat/history every 3–4s.
  const startHumanPolling = (token) => {
    if (!token) {
      console.warn('[AFGCoin Human support] startHumanPolling skipped: no session token');
      return;
    }
    console.log('[AFGCoin Human support] polling started', token);
    clearHumanPolling();
    const POLL_INTERVAL_MS = 4000;
    const poll = async () => {
      try {
        const history = await chatFlow.current.getChatHistory(token);
        if (!history) return;
        // Treat backend history as source of truth: set messages to exactly what the API returned.
        if (history.messages && Array.isArray(history.messages)) {
          setMessages(history.messages);
        }
        // Show agent typing indicator ONLY in human_waiting mode
        // Guard against API accidentally returning agent_typing during AI mode
        if (chatState === 'human_waiting' && history.agent_typing) {
          setIsTyping(true);
        } else if (chatState === 'human_waiting') {
          setIsTyping(false);
        }
        
        // Stop polling when backend says session is no longer in human support; clear persisted token so next open is fresh
        if (history.requires_human === false || (history.state && history.state !== 'human_waiting')) {
          clearHumanPolling();
          setIsTyping(false);
          try {
            localStorage.removeItem(SESSION_TOKEN_KEY);
          } catch (_) {}
          if (history.state) setChatState(history.state);
        }
      } catch (err) {
        console.warn('[AFGCoin Human support] Poll failed — user may not see your reply. Check: same apiUrl as dashboard, CORS allows this page.', err?.message || err);
      }
    };
    poll(); // first poll immediately (GET /api/chat/history?session_token=...)
    humanPollingIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
  };

  // When in human_waiting with token and widget open: load initial history (so UI matches backend) then start polling.
  // Otherwise: stop polling and clear any agent typing indicator.
  useEffect(() => {
    if (chatState !== 'human_waiting' || !sessionToken || !isOpen) {
      clearHumanPolling();
      // Clear agent typing indicator when not in human_waiting mode
      // (AI typing is handled separately and only shows during ai_fallback)
      if (chatState !== 'human_waiting') {
        setIsTyping(false);
      }
    } else {
      // Load full history when opening with existing session so we show all messages (e.g. after refresh).
      // Without this, messages stay [] until first poll merges; loading here keeps UI in sync with backend.
      loadInitialHistory(sessionToken);
      startHumanPolling(sessionToken);
    }
  }, [chatState, sessionToken, isOpen]);

  // Clear polling on unmount only
  useEffect(() => {
    return () => clearHumanPolling();
  }, []);

  // DEPRECATED: resumeSession is never called - all sessions are new
  // This function is kept for reference but should never be used
  // Every chat opening creates a brand new session via user identification
  const resumeSession = async (token) => {
    // CRITICAL: This function should NEVER be called
    // Frontend always starts fresh - no session resumption
    console.warn('resumeSession called - this should never happen. Starting fresh instead.');
    // Don't use the token - just reset state
    setMessages([]);
    setSessionToken(null);
    setChatState('identification');
    setUser(null);
    setCurrentLanguage('en');
  };

  const handleUserIdentification = async (userData) => {
    setIdentificationError(null);
    try {
      setIsLoading(true);
      messagesLoadedRef.current = false; // Reset flag for new session
      const result = await chatFlow.current.identifyUser(userData);
      setSessionToken(result.session_token);
      setUser(result.user);
      setChatState(result.next_state);
      localStorage.setItem(SESSION_TOKEN_KEY, result.session_token);
      
      // CRITICAL: Do NOT load initial history - always start fresh
      // Previous conversations remain in database but are never restored to UI
      setMessages([]); // Ensure messages start empty
    } catch (error) {
      console.error('User identification failed:', error);
      const t = translations[currentLanguage] || translations.en;
      const msg =
        error && error.message === 'Connection failed' && t.errors && t.errors.connectionFailed
          ? t.errors.connectionFailed
          : (error && error.message) || t.error;
      setIdentificationError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageSelection = async (language) => {
    try {
      setIsLoading(true);
      setCurrentLanguage(language);
      const result = await chatFlow.current.updateLanguage(sessionToken, language);
      setChatState(result.next_state);
      
      // CRITICAL: Do NOT load history - language update creates system message
      // but we don't restore old messages. System messages will be added as new messages
      // if needed, but we never load previous conversation history
    } catch (error) {
      console.error('Language update failed:', error);
      // Don't replace messages on error - preserve existing state
    } finally {
      setIsLoading(false);
    }
  };

  const handleFAQSelection = async (faqId) => {
    try {
      setIsLoading(true);
      const result = await chatFlow.current.selectFAQ(sessionToken, faqId);
      
      // Update state if needed
      if (result.state) {
        setChatState(result.state);
      }
      
      // If "OTHER" was selected, transition to AI fallback
      if (faqId === 'other') {
        setChatState('ai_fallback');
      }
      
      // FAQ selection creates an assistant message - append it
      if (result.message) {
        appendMessage({
          id: result.message.id,
          session_id: result.message.session_id || sessionToken,
          role: result.message.role,
          content: result.message.content,
          metadata: result.message.metadata,
          created_at: result.message.created_at
        });
      }
    } catch (error) {
      console.error('FAQ selection failed:', error);
      // On error, don't replace messages - preserve existing state
      // The message was already appended optimistically, so no need to reload
    } finally {
      setIsLoading(false);
    }
  };

  const handleAIMessage = async (message) => {
    // CRITICAL: Prevent multiple simultaneous message sends
    // Each message must be processed independently, one at a time
    if (isSendingMessage || isLoading) {
      console.warn('Message send already in progress, ignoring duplicate request');
      return;
    }
    
    // When in human_waiting, user messages go only to the human agent (admin dashboard).
    // We still send to the backend so the message is stored and the admin sees it; we do not show any AI reply.
    const isHumanWaiting = chatState === 'human_waiting';
    
    // Validate: message must be non-empty string
    if (!message || typeof message !== 'string') {
      console.warn('Invalid message format, ignoring');
      return;
    }
    
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      console.warn('Empty message, ignoring');
      return;
    }
    
    // Create temporary user message ID for optimistic UI update
    const tempUserMessageId = `temp-${Date.now()}`;
    
    // OPTIMISTIC UPDATE: Append user message immediately (before backend response)
    // This provides instant feedback and prevents message loss
    appendMessage({
      id: tempUserMessageId,
      session_id: sessionToken,
      role: 'user',
      content: trimmedMessage,
      metadata: null,
      created_at: new Date().toISOString()
    });
    
    // Minimum typing indicator duration (ms) - ensures dots are visible even for instant responses
    const MIN_TYPING_DURATION_MS = 700;
    
    try {
      setIsSendingMessage(true);
      setIsLoading(true);
      
      // Show typing indicator only when expecting AI response (not in human_waiting)
      if (!isHumanWaiting) {
        setIsTyping(true);
        typingStartTimeRef.current = Date.now();
      }
      
      console.log(`Sending SINGLE message: length=${trimmedMessage.length}, preview='${trimmedMessage.substring(0, 50)}...'`);
      
      // Send ONLY the current message (single message per request)
      // Backend will process this message independently
      const result = await chatFlow.current.sendMessage(sessionToken, trimmedMessage);
      
      // Update state and start polling if backend escalated to human support
      if (result.requires_human) {
        setIsTyping(false); // Clear AI typing indicator immediately on escalation
        setChatState('human_waiting');
        startHumanPolling(sessionToken);
      }

      // Append assistant response only when NOT in human_waiting (AI must not respond during human support).
      // In human_waiting we sent the message so the admin sees it, but we do not show any AI reply.
      if (result.message && !isHumanWaiting) {
        // Ensure typing indicator was visible for minimum duration
        const elapsed = Date.now() - (typingStartTimeRef.current || Date.now());
        const remainingDelay = Math.max(0, MIN_TYPING_DURATION_MS - elapsed);
        
        if (remainingDelay > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingDelay));
        }
        
        setIsTyping(false);
        appendMessage({
          id: result.message.id,
          session_id: result.message.session_id || sessionToken,
          role: result.message.role,
          content: result.message.content,
          metadata: result.message.metadata,
          created_at: result.message.created_at
        });
      } else {
        setIsTyping(false);
      }
      
      // CRITICAL: Remove temp user message ID update logic
      // We no longer replace all messages or do timestamp/content matching
      // The temp message ID will remain until the next history reload
      // This prevents message loss and answer-question mismatches
      // The temp ID is harmless and will be replaced naturally when history is reloaded
    } catch (error) {
      console.error('AI message failed:', error);
      setIsTyping(false);
      // On error, remove optimistic user message only
      // Don't replace all messages - just remove the temp message
      // This prevents losing other messages during error recovery
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempUserMessageId));
    } finally {
      setIsSendingMessage(false);
      setIsLoading(false);
      typingStartTimeRef.current = null;
    }
  };

  const handleHumanRequest = async () => {
    try {
      setIsLoading(true);
      const result = await chatFlow.current.requestHuman(sessionToken);
      const token = sessionToken;
      if (!token) {
        console.warn('[AFGCoin Human support] No session token after human request');
        return;
      }
      console.log('[AFGCoin Human support] POST /api/chat/human/request succeeded, starting polling for GET /api/chat/history every 4s');

      // Start polling immediately after backend confirms escalation (before setState so effect cleanup doesn't clear it)
      startHumanPolling(token);

      setChatState('human_waiting');

      // Human request creates a system message - append it
      if (result.message) {
        appendMessage({
          id: result.message.id,
          session_id: token,
          role: result.message.role,
          content: result.message.content,
          metadata: null,
          created_at: result.message.created_at
        });
      }
    } catch (error) {
      console.error('Human request failed:', error);
      // On error, don't replace all messages - just log the error
      // This prevents losing messages during error recovery
    } finally {
      setIsLoading(false);
    }
  };

  const handleIconDragStart = (e) => {
    if (!widgetRef.current) return;
    const rect = widgetRef.current.getBoundingClientRect();
    dragStartRef.current = {
      rectLeft: rect.left,
      rectTop: rect.top,
      clientX: e.clientX,
      clientY: e.clientY
    };
    didDragRef.current = false;
    const onMove = (moveEvent) => {
      if (!dragStartRef.current) return;
      const { rectLeft, rectTop, clientX, clientY } = dragStartRef.current;
      const dx = moveEvent.clientX - clientX;
      const dy = moveEvent.clientY - clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance >= DRAG_THRESHOLD_PX) {
        didDragRef.current = true;
        const x = rectLeft + dx;
        const y = rectTop + dy;
        const clamped = clampIconPosition(x, y, isOpen);
        lastSavedPositionRef.current = clamped;
        setIconPosition(clamped);
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const didDrag = didDragRef.current;
      dragStartRef.current = null;
      if (didDrag) {
        const pos = lastSavedPositionRef.current;
        if (pos) {
          try {
            localStorage.setItem(ICON_POSITION_KEY, JSON.stringify(pos));
          } catch (_) {}
        }
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleIconClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    setIsOpen(true);
  };

  const handleCancelHuman = async () => {
    if (!sessionToken || isLoading) return;
    try {
      setIsLoading(true);
      const result = await chatFlow.current.cancelHuman(sessionToken);

      // Append system message from backend (if provided)
      if (result && result.message) {
        appendMessage({
          id: result.message.id,
          session_id: result.message.session_id || sessionToken,
          role: result.message.role,
          content: result.message.content,
          metadata: result.message.metadata || null,
          created_at: result.message.created_at
        });
      }

      // Update chat state back to AI fallback (or backend-provided state)
      if (result && result.state) {
        setChatState(result.state);
      } else {
        setChatState('ai_fallback');
      }
    } catch (error) {
      console.error('Failed to cancel human request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clampedPosition = iconPosition ? clampIconPosition(iconPosition.x, iconPosition.y, isOpen) : null;
  const widgetStyle = clampedPosition
    ? { position: 'fixed', left: clampedPosition.x, top: clampedPosition.y, bottom: 'auto', right: 'auto' }
    : undefined;
  const widgetClassName = iconPosition
    ? 'afgcoin-chatbot-widget afgcoin-chatbot-draggable'
    : `afgcoin-chatbot-widget afgcoin-chatbot-${position}`;

  return (
    <div ref={widgetRef} className={widgetClassName} style={widgetStyle}>
      {isOpen ? (
        <ChatWindow
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            if (onCloseComplete) onCloseComplete();
          }}
          chatState={chatState}
          messages={messages}
          user={user}
          language={currentLanguage}
          isLoading={isLoading}
          isTyping={isTyping}
          identificationError={identificationError}
          onUserIdentification={handleUserIdentification}
          onLanguageSelection={handleLanguageSelection}
          onFAQSelection={handleFAQSelection}
          onAIMessage={handleAIMessage}
          onHumanRequest={handleHumanRequest}
          onCancelHuman={handleCancelHuman}
          onGetFAQs={() => chatFlow.current.getFAQs(sessionToken)}
        />
      ) : (
        <ChatButton
          onClick={handleIconClick}
          onPointerDown={handleIconDragStart}
        />
      )}
    </div>
  );
};

export default ChatbotWidget;

